class Message {
  originalMessage;
  receiver;
  constructor(originalMessageEvent, receiverIdentifier) {
    this.originalMessage = originalMessageEvent;
    this.receiver = receiverIdentifier;
  }
  isNotValid() {
    return !this.isValid();
  }
  isValid() {
    if (this.originalMessage.data.messageId === void 0) {
      return false;
    }
    if (this.originalMessage.data.channel === void 0) {
      return false;
    }
    if (this.originalMessage.data.topic === void 0) {
      return false;
    }
    if (this.originalMessage.data.target === void 0) {
      return false;
    }
    if (this.originalMessage.data.payload === void 0) {
      return false;
    }
    if (this.originalMessage.data.sender === void 0) {
      return false;
    }
    if (this.originalMessage.data.persistent === void 0) {
      return false;
    }
    return true;
  }
  isPersistent() {
    return this.originalMessage.data.persistent;
  }
  isNotPersistent() {
    return !this.isPersistent();
  }
  isFromSelf() {
    return this.getSender() === this.getReceiver();
  }
  isFromHub() {
    return Object.hasOwn(this.originalMessage.data, "fromHub") && this.originalMessage.data.fromHub;
  }
  isNotFromHub() {
    return !this.isFromHub();
  }
  getOriginal() {
    return this.originalMessage;
  }
  getReceiver() {
    return this.receiver;
  }
  getId() {
    return this.originalMessage.data.messageId;
  }
  getChannel() {
    return this.originalMessage.data.channel;
  }
  getTopic() {
    return this.originalMessage.data.topic;
  }
  getTarget() {
    return this.originalMessage.data.target;
  }
  isForTarget(target) {
    return this.getTarget() === "*" || this.getTarget() === target;
  }
  isNotForTarget(target) {
    return !this.isForTarget(target);
  }
  getPayload() {
    return this.originalMessage.data.payload;
  }
  getSender() {
    return this.originalMessage.data.sender;
  }
}

const services = {};
const ServiceContainer = {
  register(serviceName, serviceInstance) {
    if (Object.hasOwn(services, serviceName)) {
      console.warn(`ServiceContainer: Service [${serviceName}] has been reassigned!`);
    }
    services[serviceName] = serviceInstance;
  },
  resolve(serviceName) {
    if (services[serviceName] === void 0) {
      throw new Error(`ServiceContainer: No service with name: ${serviceName}`);
    }
    return services[serviceName];
  }
};

const LogType = {
  DEBUG: "DEBUG",
  VERBOSE: "VERBOSE",
  INFO: "INFO",
  WARNING: "WARNING",
  ERROR: "ERROR",
  CRITICAL: "CRITICAL"
};

let _isVerbose = false;
const Log = {
  get isVerbose() {
    return _isVerbose;
  },
  set isVerbose(value) {
    _isVerbose = value;
  },
  isNotVerbose() {
    return !_isVerbose;
  },
  setVerbose(verbose) {
    _isVerbose = verbose;
  },
  debug(message, meta = void 0) {
    Log.logMessage(LogType.DEBUG, message, meta);
  },
  info(message, meta = void 0) {
    Log.logMessage(LogType.INFO, message, meta);
  },
  verbose(message, meta = void 0) {
    Log.logMessage(LogType.VERBOSE, message, meta);
  },
  warning(message, meta = void 0) {
    Log.logMessage(LogType.WARNING, message, meta);
  },
  error(message, meta = void 0) {
    Log.logMessage(LogType.ERROR, message, meta);
  },
  critical(message, meta = void 0) {
    Log.logMessage(LogType.CRITICAL, message, meta);
  },
  logMessage(level, message, meta) {
    if (level === LogType.DEBUG && !Log.isVerbose) {
      return;
    }
    if (level === LogType.VERBOSE && Log.isNotVerbose()) {
      return;
    }
    return Log.writeLogToConsole(level, message, meta);
  },
  writeLogToConsole(level, message, meta) {
    console.group("LogMessage");
    console.log(`Level: ${level}`);
    console.log(`Message: ${message}`);
    if (meta != null) {
      console.log(meta);
    }
    console.groupEnd();
  }
};

let MessageHandler$1 = class MessageHandler {
  MAX_PERSISTED_MESSAGES = 500;
  storedData = [];
  handleMessage(message) {
    if (message.isNotValid() || message.isFromHub()) {
      return;
    }
    if (message.getChannel() === "gamehub.internal") {
      return this.handleInternalMessage(message);
    }
    const hub = ServiceContainer.resolve("gamehub");
    const clients = hub.getClients();
    Hub.getHistory().record(message.getChannel(), message.getTarget(), message.getTopic(), message.getPayload());
    this.storePersistentMessageData(message);
    for (const clientIdentifier in clients) {
      const client = clients[clientIdentifier];
      if (client == null || typeof client.postMessage !== "function") {
        Log.error("handleMessage - cannot send message to client", { client, message });
        continue;
      }
      if (!(message instanceof Message)) {
        Log.error("handleMessage - message is not a Message", { client, message });
        continue;
      }
      client.postMessage({
        messageId: message.getId(),
        channel: message.getChannel(),
        topic: message.getTopic(),
        target: message.getTarget(),
        payload: message.getPayload(),
        sender: message.getSender(),
        persistent: message.isPersistent(),
        fromHub: true
      }, {
        targetOrigin: "*"
      });
    }
  }
  storePersistentMessageData(message) {
    if (!message.isPersistent()) {
      return;
    }
    this.storedData.push(message.originalMessage.data);
    if (this.storedData.length > this.MAX_PERSISTED_MESSAGES) {
      this.storedData.shift();
    }
  }
  handleInternalMessage(message) {
    if (message.getTopic() === "clientConnect") {
      this.handleClientConnect(message);
    }
  }
  handleClientConnect(message) {
    Log.debug(`Client [${message.getPayload().clientId}] [${message.getPayload().group}] connected`);
    const hub = ServiceContainer.resolve("gamehub");
    hub.addClient(message.getPayload().clientId, message.getOriginal().source);
    message.getOriginal().source?.postMessage({
      messageId: message.getId(),
      channel: "gamehub.internal",
      topic: "connectionSuccessful",
      target: message.getTarget(),
      sender: hub.getIdentifier(),
      payload: { storedData: JSON.stringify(this.storedData), identifier: message.getPayload().clientId },
      persistent: false,
      fromHub: true
    }, {
      targetOrigin: "*"
    });
  }
};

class History {
  history = [];
  record(channel, group, topic, message) {
    const record = {
      time: Date.now(),
      channel,
      group,
      topic,
      payload: message
    };
    this.history.push(record);
    if (this.history.length >= 1e3) {
      this.history.splice(0, this.history.length - 1e3);
    }
  }
  getRecords(channel = "*", group = "*", topic = "*") {
    const records = [];
    this.history.forEach((record) => {
      if (channel !== "*" && record.channel !== "*" && record.channel !== channel) {
        return;
      }
      if (group !== "*" && record.group !== "*" && record.group !== group) {
        return;
      }
      if (topic !== "*" && record.topic !== "*" && record.topic !== topic) {
        return;
      }
      records.push(record);
    });
    Log.info("Recorded messages", records);
    return records;
  }
}

const history = new History();
class Hub {
  identifier = crypto.randomUUID();
  clients = {};
  messageHandler;
  constructor() {
    this.messageHandler = new MessageHandler$1();
    window.addEventListener("message", this.handleMessage.bind(this), false);
    ServiceContainer.register("gamehub", this);
  }
  getIdentifier() {
    return this.identifier;
  }
  getClients() {
    return this.clients;
  }
  static getHistory() {
    return history;
  }
  handleMessage(messageEvent) {
    return this.messageHandler.handleMessage(
      new Message(messageEvent, this.identifier)
    );
  }
  addClient(identifier, source) {
    if (Object.hasOwn(this.clients, identifier)) {
      throw new Error(
        `Hub.addClient - Client with identifier [${identifier}] is already known!`
      );
    }
    if (source == null || typeof source.postMessage !== "function") {
      throw new Error(
        `Hub.addClient - provided source is null or lacks postMessage function ${source}`
      );
    }
    this.clients[identifier] = source;
  }
}

class MessageHandler {
  listeners = /* @__PURE__ */ new Map();
  knownMessageIds = [];
  parent;
  isConnected = false;
  storedMessages = /* @__PURE__ */ new Map();
  MAX_PERSISTED_MESSAGES_PER_TOPIC = 50;
  constructor(parent) {
    this.parent = parent;
    setTimeout(() => {
      if (this.isConnected === false) {
        Log.error(
          "GameHubClient did not receive ConnectionSuccessful from a GameHub"
        );
      }
    }, 2e3);
  }
  handleMessage(message) {
    Log.verbose(`Got message with topic "${message.getTopic()}"`, {
      message
    });
    if (message.isNotValid() || message.isFromSelf() || message.isNotFromHub()) {
      Log.verbose(
        `Message is discarded isNotValid? ${message.isNotValid()} isFromSelf? ${message.isFromSelf()} isNotFromHub? ${message.isNotFromHub()}`
      );
      return;
    }
    if (message.getChannel() === "gamehub.internal") {
      Log.verbose(
        `Message with topic "${message.getTopic()}" is sent over the gamehub.internal channel`
      );
      return this.handleInternalMessage(message);
    }
    if (this.messageIsKnown(message)) {
      Log.verbose(
        `Message with topic "${message.getTopic()}" is already known`
      );
      return;
    }
    return this.handleListener(message);
  }
  handleInternalMessage(message) {
    if (message.getTopic() === "connectionSuccessful") {
      this.handleConnectionSuccessful(message);
    }
  }
  handleListener(message) {
    if (message.isNotForTarget(this.parent.options.group)) {
      Log.verbose(
        `The target (${message.getTarget()}) for the message with topic "${message.getTopic()}" is not my target (${this.parent.options.group})`
      );
      return;
    }
    if (this.doesNotHaveListener(message)) {
      Log.verbose(
        `No listener is defined for the message with topic "${message.getTopic()}"`
      );
      this.storePersistentMessage(message);
      return;
    }
    this.flagMessageAsKnown(message);
    Log.verbose(`Message with topic "${message.getTopic()}" is handled`);
    const handler = this.getListener(message.getChannel(), message.getTopic());
    if (handler) {
      handler(message.getPayload());
    }
  }
  storePersistentMessage(message) {
    if (message.isNotPersistent()) {
      return;
    }
    if (message.isNotForTarget(this.parent.options.group)) {
      Log.verbose(
        `Not storing message with topic ${message.getTopic()} as it is not for my group`
      );
      return;
    }
    const channel = message.getChannel();
    const topic = message.getTopic();
    if (!this.storedMessages.has(channel)) {
      this.storedMessages.set(channel, /* @__PURE__ */ new Map());
    }
    const channelMessages = this.storedMessages.get(channel);
    if (!channelMessages.has(topic)) {
      channelMessages.set(topic, []);
    }
    const messages = channelMessages.get(topic);
    messages.push(message);
    if (messages.length > this.MAX_PERSISTED_MESSAGES_PER_TOPIC) {
      messages.shift();
    }
  }
  handlePersistentMessages(channel, topic, callback) {
    const messages = this.storedMessages.get(channel)?.get(topic);
    if (!messages) {
      return;
    }
    for (const message of messages) {
      Log.verbose(
        `Calling back persisted message with topic ${message.getTopic()}`
      );
      callback(message.getPayload());
    }
  }
  getListener(channel, topic) {
    return this.listeners.get(channel)?.get(topic);
  }
  hasListener(message) {
    return this.getListener(message.getChannel(), message.getTopic()) !== void 0;
  }
  doesNotHaveListener(message) {
    return !this.hasListener(message);
  }
  flagMessageAsKnown(message) {
    while (this.knownMessageIds.length >= 25) {
      this.knownMessageIds.shift();
    }
    this.knownMessageIds.push(message.getId());
  }
  messageIsKnown(message) {
    return this.knownMessageIds.includes(message.getId());
  }
  addListener(channel, topic, callback) {
    if (!this.listeners.has(channel)) {
      this.listeners.set(channel, /* @__PURE__ */ new Map());
    }
    const channelListeners = this.listeners.get(channel);
    if (channelListeners.has(topic)) {
      Log.warning(
        `A listener has already been assigned for [${channel}.${topic}]`
      );
      return;
    }
    channelListeners.set(topic, callback);
  }
  handleConnectionSuccessful(message) {
    this.isConnected = true;
    const payload = message.getPayload();
    const identifier = payload.identifier;
    const storedData = JSON.parse(payload.storedData);
    for (const data of storedData) {
      const message2 = new Message({ data }, identifier);
      this.storePersistentMessage(message2);
      if (message2.isForTarget(this.parent.options.group)) {
        const handler = this.getListener(message2.getChannel(), message2.getTopic());
        if (handler) {
          handler(message2.getPayload());
        }
      }
    }
    Log.debug(
      `Connection Successful! Added ${storedData.length} persisted messages`
    );
  }
}

class HubClient {
  identifier = crypto.randomUUID();
  options = {
    channel: "engage",
    group: `group-${crypto.randomUUID()}`,
    origin: "*"
  };
  messageHandler;
  constructor(options = {}) {
    Object.assign(this.options, options);
    this.messageHandler = new MessageHandler(this);
    window.addEventListener("message", this.handleMessage.bind(this), false);
    this.connect();
    ServiceContainer.register("client", this);
  }
  handleMessage(messageEvent) {
    return this.messageHandler.handleMessage(
      new Message(messageEvent, this.identifier)
    );
  }
  subscribeTo(channel, topic, callback) {
    this.messageHandler.addListener(channel, topic, callback);
    this.messageHandler.handlePersistentMessages(channel, topic, callback);
  }
  publish(topic, payload = {}, persistent = false) {
    const message = {
      messageId: crypto.randomUUID(),
      channel: this.options.channel,
      topic,
      target: this.options.group,
      payload,
      sender: this.identifier,
      persistent
    };
    window.top.postMessage(message, this.options.origin);
  }
  publishTo(topic, target, payload = {}, persistent = false) {
    const message = {
      messageId: crypto.randomUUID(),
      channel: this.options.channel,
      topic,
      target,
      payload,
      sender: this.identifier,
      persistent
    };
    window.top.postMessage(message, this.options.origin);
  }
  broadcast(topic, payload = {}) {
    const message = {
      messageId: crypto.randomUUID(),
      channel: this.options.channel,
      topic,
      target: "*",
      payload,
      sender: this.identifier,
      persistent: false
    };
    window.top.postMessage(message, this.options.origin);
  }
  connect() {
    const message = {
      messageId: crypto.randomUUID(),
      channel: "gamehub.internal",
      topic: "clientConnect",
      target: this.options.group,
      sender: this.identifier,
      payload: {
        clientId: this.identifier,
        group: this.options.group
      },
      persistent: false
    };
    window.top.postMessage(message, this.options.origin);
  }
  setVerboseLogging(verbose) {
    Log.setVerbose(verbose);
  }
}

const ENGAGE_CHANNEL = {
  engage: "engage",
  game: "game",
  panel: "panel",
  scratchcard: "scratchcard",
  wheelOfFortune: "wheel_of_fortune"
};
const ENGAGE_TOPIC = {
  triggerEvent: "trigger.event",
  pauseGame: "pause.game",
  continueGame: "continue.game",
  moveGame: "moveGame",
  resizeEngage: "resizeEngage",
  freeSpinsAccept: "freeSpinsAccept",
  freeSpinsEnded: "freeSpinsEnded"
};
const GAME_TOPIC = {
  showDialog: "show.dialog",
  engageData: "game.engageData",
  gameReady: "game.ready"
};
const PANEL_TOPIC = {
  windowResize: "window.resize",
  focusIn: "focusIn",
  focusOut: "focusOut"
};
const SCRATCHCARD_TOPIC = {
  ready: "scratchCardReady",
  closed: "scratchCardClosed"
};
const WHEEL_OF_FORTUNE_TOPIC = {
  ready: "wheelOfFortuneReady",
  closed: "wheelOfFortuneClosed"
};

export { HubClient as Client, ENGAGE_CHANNEL, ENGAGE_TOPIC, GAME_TOPIC, Hub as GameHub, HubClient as GameHubClient, Hub, PANEL_TOPIC, SCRATCHCARD_TOPIC, WHEEL_OF_FORTUNE_TOPIC };
